import React, { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { GridFilterOperator, GridFilterInputValueProps } from '@mui/x-data-grid-pro';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { DateRange } from '@mui/x-date-pickers-pro';

/**
 * Custom GridFilterOperator Component for type 'date'.
 */
const DateFilter = (props: GridFilterInputValueProps) => {
    /** Deconstruct props. */
    const { item, applyValue } = props;

    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'docTable' });

    /** State slice that controls the value of the Date Range. */
    const [dateValue, setDateValue] = useState<DateRange<Dayjs>>([null, null]);

    /** Effect that sets the value of the Date Range. */
    useEffect(() => {
        item.value && setDateValue(item.value);
    }, [item.value]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateRangePicker
                slotProps={{
                    textField: ({ position }) => ({
                        variant: 'standard',
                        label: position === 'start' ? t('from') : t('to'),
                    }),
                }}
                value={dateValue}
                onChange={newValue => {
                    setDateValue(newValue);
                    applyValue({ ...item, value: newValue });
                }}
            />
        </LocalizationProvider>
    );
};

/** Custom date filter operator. */
export const dateFilterOperators = (translateFn: TFunction): GridFilterOperator[] =>
    (() => {
        const filters = [];

        filters.push({
            label: translateFn('between'),
            headerLabel: translateFn('between'),
            value: 'between',
            getApplyFilterFn: filterItem => {
                if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
                    return null;
                }
                if (filterItem.value[0] === null || filterItem.value[1] === null) {
                    return null;
                }
                return (value: string | number): boolean => {
                    const date = typeof value === 'string' ? value.split('|')[0] : value;

                    return (
                        date !== null &&
                        filterItem.value[0] <= dayjs(+date) &&
                        dayjs(+date) <= filterItem.value[1]
                    );
                };
            },
            InputComponent: DateFilter,
        });

        return filters;
    })();

export default DateFilter;
